/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener, FileOpenerOptions } from "@capacitor-community/file-opener";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackButton } from "../../components/BackButton";
import { Button } from "../../components/Button";
import { PageContainer } from "../../components/layout/PageContainer";
import { Loader } from "../../components/Loader";
import TitleBox from "../../components/TitleBox";
import ToastIcon from "../../components/ToastIcon";
import TestIds from "../../testIds";
import { CONTENT_LAYOUT_WIDTH } from "../../util/consts";
import { htmlElementToPdf } from "../../util/htmlElementToPdf";
import useToast from "../../util/useToast";

import SeedEditPriceProvider from "./components/useSeedEditPriceModal";
import { SeedOrderSummaryDetails, SeedOrderSummaryForm } from "./form/SeedOrderSummaryForm";

const PDF_FILENAME = "FarmlandsPRO_seed_order_summary.pdf";

export const SeedOrderSummary = () => {
  const componentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { notify } = useToast();

  const [printLoading, setPrintLoading] = useState(false);

  const handlePrintOrders = useCallback(async () => {
    const el = componentRef.current;
    if (!el) {
      return;
    }

    setPrintLoading(true);

    try {
      const pdf = await htmlElementToPdf(el);

      if (Capacitor.isNativePlatform()) {
        const dataUri = pdf.output("datauristring");
        const comma = dataUri.indexOf(",");
        const base64 = comma >= 0 ? dataUri.slice(comma + 1) : "";

        const permission = await Filesystem.requestPermissions();
        if (!permission) {
          return;
        }
        const result = await Filesystem.writeFile({
          path: PDF_FILENAME,
          data: base64,
          directory: Directory.Documents,
        });
        const fileOpenerOptions: FileOpenerOptions = {
          filePath: result.uri,
          contentType: "application/pdf",
          openWithDefault: true,
        };
        await FileOpener.open(fileOpenerOptions);
      } else {
        pdf.save(PDF_FILENAME);
      }
    } catch {
      notify("We couldn't create or open your PDF. Please try again.", {
        type: "error",
        icon: <ToastIcon>Error</ToastIcon>,
      });
    } finally {
      setPrintLoading(false);
    }
  }, [notify]);

  const [generatedTime, setGeneratedTime] = useState("");

  useEffect(() => {
    const date = dayjs();
    setGeneratedTime(`Generated on ${date.format("DD/MM/YY")} at ${date.format("hh:mm a")}`);
  }, []);

  const theme = useTheme();

  return (
    <PageContainer
      data-testid={TestIds.pages.seedOrderSummary}
      sx={{ maxWidth: `${CONTENT_LAYOUT_WIDTH}px` }}
    >
      <SeedEditPriceProvider>
        <BackButton text="Seed" onClick={() => navigate("/seed")} />
        <div ref={componentRef}>
          <Box pb={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Stack>
                <TitleBox display="Order Summary" />
                <Typography variant="body2" color={theme.colors.primary500} mt={1}>
                  {generatedTime}
                </Typography>
              </Stack>
              {printLoading ? (
                <Loader title="Printing" type="inline-flex" />
              ) : (
                <Button
                  variant="contained"
                  onClick={handlePrintOrders}
                  disabled={printLoading}
                  data-html2canvas-ignore={true}
                >
                  Print orders
                </Button>
              )}
            </Stack>
          </Box>
          <SeedOrderSummaryDetails />
        </div>
        <SeedOrderSummaryForm />
      </SeedEditPriceProvider>
    </PageContainer>
  );
};
